import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useContext, useState } from 'react';
import { createProduct, validateProduct } from '../../../services/productService';
import ToastContext from '../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import { serverErrorsToFieldMap } from '../../../helpers/productValidation';
import ProductForm from '../../UI/Products/ProductForm';

/**
 * Alta manual de un producto.
 *
 * El flujo es validar → crear, a propósito:
 *   1. `POST /products/validate` es un dry-run que no escribe nada y devuelve
 *      TODOS los errores mapeados a su campo (incluidos los que solo sabe el
 *      servidor: familia, SKU, EAN o UPC ya usados).
 *   2. Solo si eso pasa limpio se hace el `POST /products`.
 *
 * Así el usuario nunca pierde lo que lleva escrito por un error de validación,
 * que era el requisito. El back repite la validación dentro de su transacción,
 * porque entre el paso 1 y el 2 hay una ventana de carrera.
 */
const NewProduct = () => {
  const history = useHistory();
  const [, dispatchToast] = useContext(ToastContext);
  const [serverErrors, setServerErrors] = useState({});

  const mutation = useMutation(
    async (data) => {
      const { data: validation } = await validateProduct(data);

      if (!validation.valid) {
        const fieldErrors = serverErrorsToFieldMap(validation.errors);
        // Se propaga para que `onError` lo distinga de un fallo de red.
        const error = new Error('VALIDATION_FAILED');
        error.fieldErrors = fieldErrors;
        error.errorCount = validation.errors.length;
        throw error;
      }

      const { data: created } = await createProduct(data);
      return created;
    },
    {
      onMutate: () => setServerErrors({}),
      onSuccess: (created) => {
        dispatchToast({ type: SET_OK, payload: { message: 'Producto creado correctamente' } });
        history.push(`/products/${created.data?.id || ''}`);
      },
      onError: (e) => {
        if (e.fieldErrors) {
          setServerErrors(e.fieldErrors);
          dispatchToast({
            type: SET_ERROR,
            payload: {
              message:
                e.errorCount === 1
                  ? 'Hay 1 campo con errores. No se ha creado nada.'
                  : `Hay ${e.errorCount} campos con errores. No se ha creado nada.`,
            },
          });
          return;
        }

        dispatchToast({
          type: SET_ERROR,
          payload: { message: e.response?.data?.message || 'Error al crear producto' },
        });
      },
    },
  );

  const handleSubmit = ({ type, data }) => {
    // En alta el único tipo que llega es `save_product`: las variantes y las
    // imágenes se gestionan en local hasta que el producto existe.
    if (type !== 'save_product') return;
    mutation.mutate(data);
  };

  return (
    <ProductForm
      title="Nuevo producto"
      subtitle="Alta manual. El producto no se publica en Shopify."
      backAction={{ content: 'Productos', url: '/products' }}
      isNew
      onSubmit={handleSubmit}
      isLoading={mutation.isLoading}
      serverErrors={serverErrors}
    />
  );
};

export default NewProduct;
